import { api } from './api';
import { apiPreload } from './preload';
/** Load em voo: colapsa pedidos concorrentes do mesmo carrinho. */
let inflight = null;

/** Quando o estado atual foi obtido (preload do boot conta). 0 = nunca. */
let loadedAt = 0;
// Cache persistido em localStorage para que o contador e o estado "não lido"
// apareçam instantaneamente ao recarregar a página, sem esperar o fetch.
const UNREAD_KEY = 'marketplace_cart_unread';
const TOTALS_KEY = 'marketplace_cart_totals';
function safeParse(raw, fallback) {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}
function readUnread() {
  try {
    const v = localStorage.getItem(UNREAD_KEY);
    const n = v ? parseInt(v, 10) : 0;
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}
function writeUnread(n) {
  try {
    if (n > 0) localStorage.setItem(UNREAD_KEY, String(n));else localStorage.removeItem(UNREAD_KEY);
  } catch {
    /* storage disabled */
  }
}
function readTotals() {
  try {
    const v = safeParse(localStorage.getItem(TOTALS_KEY), {});
    return {
      subtotal: Number(v.subtotal) || 0,
      shipping: Number(v.shipping) || 0,
      total: Number(v.total) || 0,
      count: Number(v.count) || 0
    };
  } catch {
    return {
      subtotal: 0,
      shipping: 0,
      total: 0,
      count: 0
    };
  }
}
function writeTotals(t) {
  try {
    if ((t.count || 0) > 0) localStorage.setItem(TOTALS_KEY, JSON.stringify(t));else localStorage.removeItem(TOTALS_KEY);
  } catch {
    /* storage disabled */
  }
}
const state = {
  items: [],
  totals: readTotals(),
  loading: false
};
let unread = readUnread();
const listeners = [];
function emit() {
  listeners.forEach(l => l(state));
  if (typeof m !== 'undefined' && m.redraw) {
    try {
      m.redraw();
    } catch {
      /* not in redraw cycle */
    }
  }
}
export const cartStore = {
  get: () => state,
  getUnread: () => unread,
  markSeen() {
    if (unread === 0) return;
    unread = 0;
    writeUnread(0);
    emit();
  },
  subscribe(l) {
    listeners.push(l);
    return () => {
      const i = listeners.indexOf(l);
      if (i > -1) listeners.splice(i, 1);
    };
  },
  /**
   * Carrega o carrinho. Serve o preload do boot quando ele existe (rotas do
   * marketplace embutem o cart no HTML) e, fora isso, deduplica chamadas
   * concorrentes: no boot da loja, o initializer e o DiscoverView pedem o
   * carrinho no mesmo tick — sem isso são dois requests idênticos.
   *
   * `force` pula a deduplicação: mutações (add/update/remove) precisam do
   * estado DEPOIS da escrita, nunca de um fetch que começou antes dela.
   */
  async load(force) {
    if (force === void 0) {
      force = false;
    }
    if (!force && inflight) return inflight;
    const run = async () => {
      state.loading = true;
      emit();
      try {
        const r = await apiPreload('cart', '/marketplace/cart');
        state.items = r.data ?? [];
        state.totals = r.totals ?? state.totals;
        writeTotals(state.totals);
        if ((state.totals.count || 0) === 0 && unread !== 0) {
          unread = 0;
          writeUnread(0);
        }
        loadedAt = Date.now();
      } finally {
        state.loading = false;
        emit();
      }
    };
    if (force) return run();
    inflight = run().finally(() => {
      inflight = null;
    });
    return inflight;
  },
  /**
   * Carrega só se o estado atual já estiver velho. Existe porque o carrinho é
   * pedido em dois pontos no mesmo carregamento de página (initializer do boot
   * e a página do marketplace que monta em seguida) — com o preload do boot o
   * segundo pedido seria um round-trip para reconfirmar o que acabou de chegar.
   * Navegação SPA depois da janela continua buscando estado fresco.
   */
  async ensureFresh(maxAgeMs) {
    if (maxAgeMs === void 0) {
      maxAgeMs = 15_000;
    }
    if (loadedAt && Date.now() - loadedAt < maxAgeMs) return;
    return this.load();
  },
  async add(productId, quantity, cycle, licenseType) {
    if (quantity === void 0) {
      quantity = 1;
    }
    // cycle só importa pra produtos com dual-billing — backend ignora
    // pra demais (cai no billing_cycle do produto).
    const body = {
      product_id: productId,
      quantity
    };
    if (cycle === 'monthly' || cycle === 'yearly') body.cycle = cycle;
    if (licenseType === 'extended') body.license_type = 'extended';
    const r = await api('POST', '/marketplace/cart/items', body);
    state.totals = r.totals ?? state.totals;
    writeTotals(state.totals);
    unread = unread + quantity;
    writeUnread(unread);
    await this.load(true);
    return r;
  },
  async addBundle(bundleId, quantity) {
    if (quantity === void 0) {
      quantity = 1;
    }
    const r = await api('POST', '/marketplace/cart/items', {
      bundle_id: bundleId,
      quantity
    });
    state.totals = r.totals ?? state.totals;
    writeTotals(state.totals);
    unread = unread + quantity;
    writeUnread(unread);
    await this.load(true);
    return r;
  },
  async update(itemId, quantity) {
    // Mutação otimista — UI responde imediato, antes do round-trip de rede.
    // Em caso de falha, reverte pro valor anterior e re-emite pra view
    // voltar ao estado correto.
    const found = state.items.find(i => i.id === itemId);
    const previousQty = found?.quantity;
    const previousTotals = {
      ...state.totals
    };
    if (found) found.quantity = quantity;
    emit();
    try {
      const r = await api('PATCH', `/marketplace/cart/items/${itemId}`, {
        quantity
      });
      state.totals = r.totals ?? state.totals;
      writeTotals(state.totals);
      emit();
      return r;
    } catch (e) {
      if (found && previousQty !== undefined) found.quantity = previousQty;
      state.totals = previousTotals;
      writeTotals(state.totals);
      emit();
      throw e;
    }
  },
  async remove(itemId) {
    // Snapshot do item pra restaurar em caso de falha (rare mas possível
    // — backend pode retornar 409 se item já foi removido em outra tab).
    const previous = state.items.find(i => i.id === itemId);
    try {
      await api('DELETE', `/marketplace/cart/items/${itemId}`);
      state.items = state.items.filter(i => i.id !== itemId);
      await this.load(true);
    } catch (e) {
      // Não removeu nada do state ainda, só relança pra caller tratar
      throw e;
    }
  },
  async clear() {
    const previousItems = state.items.slice();
    const previousTotals = {
      ...state.totals
    };
    try {
      await api('DELETE', '/marketplace/cart');
      state.items = [];
      state.totals = {
        subtotal: 0,
        shipping: 0,
        total: 0,
        count: 0
      };
      writeTotals(state.totals);
      unread = 0;
      writeUnread(0);
      emit();
    } catch (e) {
      // Nada foi mutado ainda (writes vêm DEPOIS do api()), mas se algum
      // efeito colateral disparou, restaura ao estado anterior pra UI
      // não ficar inconsistente.
      state.items = previousItems;
      state.totals = previousTotals;
      emit();
      throw e;
    }
  }
};
flarum.reg.add('ramon-marketplace', 'common/utils/cartStore', { cartStore: cartStore, });